import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {applyTheme, checkAvailability, closeChat, initChat, setInlineMode, setContext, fetchSuggestions} from "../../slice/chat.slice";
import {Config} from "../../config";
import {StartChatButton} from "./start.chat.button";
import {store} from "../../store/store";
import {Chat} from "./chat";
import {ChatError} from "./chat.error";

export const ChatContainer = () => {
    const dispatch = useDispatch();
    const chatState = useSelector((state) => state.chat);
    const config = Config.getInstance();

    const [hideTooltip, setHideTooltip] = useState(false);

    useEffect(() => {
        dispatch(applyTheme());
        dispatch(setInlineMode());
        dispatch(checkAvailability());

        if (localStorage.getItem("chatButtonClicked") === "true") {
            setHideTooltip(true);
        }

        window.SBChatWidget = {
            setContext: function(ctx) {
                store.dispatch(setContext(ctx));
                store.dispatch(fetchSuggestions(ctx));
            }
        };

        return () => {
            delete window.SBChatWidget;
        };
    }, [dispatch]);

    // Auto-initialize chat in inline mode or when resuming an existing session.
    // If the user explicitly closed the chat (ai_chat_closed_by_user flag), do
    // NOT auto-reconnect on page navigation — wait for them to open the chat
    // again. The session id itself is kept so that the manual re-open can still
    // restore the previous conversation via hibernation/reconnect.
    useEffect(() => {
        if (chatState.available && !chatState.chatStarted && !chatState.error) {
            const userClosed = sessionStorage.getItem('ai_chat_closed_by_user');
            const hasPrevSession = sessionStorage.getItem('ai_chat_session_id');
            if (chatState.inlineMode || (hasPrevSession && !userClosed)) {
                dispatch(initChat());
            }
        }
    }, [dispatch, chatState.inlineMode, chatState.available, chatState.chatStarted, chatState.error]);

    // Fetch default suggestions when chat starts and no context has been set
    useEffect(() => {
        if (chatState.chatStarted && !chatState.context && chatState.suggestions.length === 0) {
            dispatch(fetchSuggestions({}));
        }
    }, [dispatch, chatState.chatStarted]);

    // Toggle body class for sidebar mode
    useEffect(() => {
        if (chatState.sidebarMode && chatState.chatStarted) {
            document.body.classList.add('sb-chat-sidebar-open');
        } else {
            document.body.classList.remove('sb-chat-sidebar-open');
        }
    }, [chatState.sidebarMode, chatState.chatStarted]);

    // Hide body scrollbars when admin chat is in full-page mode
    useEffect(() => {
        if (config.is_admin && chatState.chatStarted && !chatState.sidebarMode) {
            document.body.classList.add('without-scrollbars');
        } else {
            document.body.classList.remove('without-scrollbars');
        }
        return () => {
            document.body.classList.remove('without-scrollbars');
        };
    }, [chatState.chatStarted, chatState.sidebarMode]);

    const startChat = () => {
        sessionStorage.removeItem('ai_chat_closed_by_user');
        dispatch(initChat());

        localStorage.setItem("chatButtonClicked", "true");
        setHideTooltip(true);
    };
    const onCloseChat = () => {
        dispatch(closeChat());
    };

    if (chatState.chatStarted) {
        return <Chat onClose={onCloseChat} inlineMode={chatState.inlineMode} sidebarMode={chatState.sidebarMode} isAdmin={config.is_admin} />;
    }
    if (chatState.error) {
        return <ChatError message={chatState.error} onClose={onCloseChat} inlineMode={chatState.inlineMode} sidebarMode={chatState.sidebarMode} isAdmin={config.is_admin} />;
    }
    if (chatState.available) {
        // In inline mode, show nothing until chat is started
        if (chatState.inlineMode) {
            return <></>;
        }
        return <StartChatButton onClick={startChat} hideTooltip={hideTooltip}/>;
    }
    return <></>;
}